import { defineStore, acceptHMRUpdate } from "pinia";
import { LocalStorage } from "quasar";
import { CapacitorHttp } from "@capacitor/core";

import type { AuthUser } from "@/models/authUser";
import { useAppConfigStore } from "./appConfig";
import { APIError, AppError, errorFromUnknown } from "@/errors";
import { isNonNullableObject, propGuard } from "@/types";
import { wait } from "@/utility.js";

const initState: AuthUser & {
    token: string | null;
    receiptAuthenticated: boolean;
    receiptLoginPending: boolean;
} = {
    id: -1,
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    subscriptions: [],
    ppvPurchases: [],
    token: null,
    receiptAuthenticated: false,
    receiptLoginPending: false
};

const OAUTH_REFRESH_TOKEN_KEY = "auth.oauth.refreshToken";
const OAUTH_PKCE_VERIFIER_KEY = "oauth.pkce.verifier";
const OAUTH_STATE_KEY = "oauth.pkce.state";

interface OAuthDiscoveryDocument {
    authorization_endpoint: string;
    token_endpoint: string;
    device_authorization_endpoint?: string;
}

const oauthErrorCodeDisplayHash = {
    access_denied: "Sign-in was canceled or denied. Please try again.",
    authorization_pending: "Waiting for authorization to complete.",
    slow_down: "Waiting for authorization to complete.",
    expired_token: "This device code has expired. Please start over.",
    invalid_client:
        "This app is not configured correctly for this sign-in provider.",
    unauthorized_client:
        "This app is not configured correctly for this sign-in provider.",
    invalid_grant:
        "Your sign-in session is invalid or expired. Please try again.",
    invalid_request:
        "The sign-in request was rejected by the provider. Please try again.",
    server_error:
        "The sign-in service is temporarily unavailable. Please try again.",
    temporarily_unavailable:
        "The sign-in service is temporarily unavailable. Please try again.",
    unsupported_grant_type:
        "This sign-in method is not supported by the provider.",
    unsupported_response_type:
        "This sign-in method is not supported by the provider.",
    unknown: "An unknown error occurred during sign-in. Please try again."
} as const;

type OAuthErrorCode = keyof typeof oauthErrorCodeDisplayHash;

export interface OAuthDeviceAuthorizationResponse {
    device_code: string;
    user_code: string;
    verification_uri: string;
    verification_uri_complete?: string;
    expires_in: number;
    interval?: number;
}

let oauthDiscoveryCache:
    | {
          url: string;
          document: OAuthDiscoveryDocument;
      }
    | undefined;

function createRandomString(size = 64): string {
    const bytes = new Uint8Array(size);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, value => (value % 36).toString(36)).join("");
}

function toBase64Url(bytes: ArrayBuffer): string {
    const chars =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
    let output = "";
    const buffer = new Uint8Array(bytes);
    for (let index = 0; index < buffer.length; index += 3) {
        const a = buffer[index] ?? 0;
        const b = buffer[index + 1] ?? 0;
        const c = buffer[index + 2] ?? 0;
        const triple = (a << 16) | (b << 8) | c;
        output += chars[(triple >> 18) & 63] ?? "";
        output += chars[(triple >> 12) & 63] ?? "";
        output +=
            index + 1 < buffer.length ? (chars[(triple >> 6) & 63] ?? "") : "=";
        output += index + 2 < buffer.length ? (chars[triple & 63] ?? "") : "=";
    }
    return output.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

async function createPkceChallenge(verifier: string): Promise<string> {
    const hashed = await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(verifier)
    );
    return toBase64Url(hashed);
}

function getOAuthConfigOrThrow() {
    const oauth = useAppConfigStore().features.authentication.settings.oauth;
    if (!oauth?.discoveryUrl) {
        throw new AppError("OAuth settings are not configured correctly.", {
            code: "incorrect_config"
        });
    }
    return oauth;
}

async function loadOAuthDiscovery(): Promise<OAuthDiscoveryDocument> {
    const oauth = getOAuthConfigOrThrow();
    if (oauthDiscoveryCache?.url === oauth.discoveryUrl) {
        return oauthDiscoveryCache.document;
    }

    const data = await oauthGetJson(oauth.discoveryUrl);
    if (
        !isNonNullableObject(data) ||
        !propGuard(data, "authorization_endpoint", "string") ||
        !propGuard(data, "token_endpoint", "string")
    ) {
        throw new AppError(
            "OAuth discovery results could not be parsed correctly.",
            {
                code: "parse_error"
            }
        );
    }

    const document: OAuthDiscoveryDocument = {
        authorization_endpoint: data.authorization_endpoint,
        token_endpoint: data.token_endpoint,
        ...(propGuard(data, "device_authorization_endpoint", "string")
            ? {
                  device_authorization_endpoint:
                      data.device_authorization_endpoint
              }
            : {})
    };
    oauthDiscoveryCache = {
        url: oauth.discoveryUrl,
        document
    };
    return document;
}

function canceledError(): APIError {
    return new APIError("", { status: 0, errors: [{ code: "canceled" }] });
}

function getOAuthRedirectUri(currentUrl: string): string {
    const url = new URL(currentUrl);
    const hashPath = url.hash ? url.hash.split("?")[0] : "#/login";
    return `${url.origin}${url.pathname}${hashPath}`;
}

function getOAuthCallbackParam(
    name: string,
    routeQuery: Record<string, unknown>,
    currentUrl: string
): string | undefined {
    const routeParam = routeQuery[name];
    if (typeof routeParam === "string" && routeParam.length > 0) {
        return routeParam;
    }

    const searchValue = new URL(currentUrl).searchParams.get(name);
    return searchValue && searchValue.length > 0 ? searchValue : undefined;
}

function clearOAuthCallbackParams(currentUrl: string): string {
    const url = new URL(currentUrl);
    url.searchParams.delete("code");
    url.searchParams.delete("state");
    url.searchParams.delete("error");
    url.searchParams.delete("error_description");
    return url.toString();
}

function oauthStatusFromCode(error: string): number {
    switch (error) {
        case "access_denied":
        case "unauthorized_client":
            return 403;
        case "invalid_client":
        case "invalid_grant":
        case "expired_token":
            return 401;
        case "server_error":
        case "temporarily_unavailable":
            return 503;
        default:
            return 400;
    }
}

function isOAuthErrorCode(code: unknown): code is OAuthErrorCode {
    return typeof code === "string" && code in oauthErrorCodeDisplayHash;
}

function oauthErrorFromResponse(response: {
    status: number;
    data: unknown;
}): AppError {
    const message =
        isNonNullableObject(response.data) &&
        propGuard(response.data, "error_description", "string")
            ? response.data.error_description
            : `OAuth request failed with status ${response.status}`;
    const code =
        isNonNullableObject(response.data) &&
        propGuard(response.data, "error", "string") &&
        isOAuthErrorCode(response.data.error)
            ? response.data.error
            : "unknown";
    return new AppError(message, {
        code: "unknown",
        display: oauthErrorCodeDisplayHash[code],
        cause: response.data
    });
}

function parseOAuthBody(data: unknown): unknown {
    if (typeof data !== "string") {
        return data;
    }

    const trimmed = data.trim();
    if (trimmed.length === 0) {
        return undefined;
    }

    try {
        return JSON.parse(trimmed) as unknown;
    } catch {
        return data;
    }
}

function parseOAuthResponseOrThrow(status: number, data: unknown): unknown {
    if (status >= 200 && status < 300) {
        return data;
    }

    throw oauthErrorFromResponse({ status, data });
}

async function oauthGetJson(url: string): Promise<unknown> {
    if (import.meta.env.QUASAR_CAPACITOR_MODE) {
        const response = await CapacitorHttp.get({ url });
        const parsed = parseOAuthBody(response.data);
        return parseOAuthResponseOrThrow(response.status, parsed);
    }

    const response = await fetch(url);
    const data = await response.json().catch(() => undefined);
    return parseOAuthResponseOrThrow(response.status, data);
}

async function oauthPostForm(
    url: string,
    body: URLSearchParams
): Promise<{
    status: number;
    data: unknown;
}> {
    if (import.meta.env.QUASAR_CAPACITOR_MODE) {
        const response = await CapacitorHttp.post({
            url,
            headers: {
                "Content-Type": "application/x-www-form-urlencoded"
            },
            data: body.toString()
        });
        return {
            status: response.status,
            data: parseOAuthBody(response.data)
        };
    }

    const response = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded"
        },
        body
    });

    return {
        status: response.status,
        data: await response.json().catch(() => undefined)
    };
}

function parseOAuthTokenResponse(data: unknown): {
    accessToken: string;
    refreshToken?: string;
} {
    if (
        !isNonNullableObject(data) ||
        !propGuard(data, "access_token", "string")
    ) {
        throw new AppError(
            "OAuth token response did not include an access token.",
            {
                code: "parse_error"
            }
        );
    }

    return {
        accessToken: data.access_token,
        ...(propGuard(data, "refresh_token", "string")
            ? { refreshToken: data.refresh_token }
            : {})
    };
}

async function getSecureStorage() {
    if (!import.meta.env.QUASAR_CAPACITOR_MODE) {
        return null;
    }

    return await import("@aparajita/capacitor-secure-storage");
}

async function getStoredOAuthRefreshToken(): Promise<string | null> {
    const secureStorage = await getSecureStorage();
    if (!secureStorage) {
        return null;
    }

    const token = await secureStorage.SecureStorage.get(
        OAUTH_REFRESH_TOKEN_KEY
    );
    return typeof token === "string" && token.length > 0 ? token : null;
}

async function setStoredOAuthRefreshToken(refreshToken: string): Promise<void> {
    const secureStorage = await getSecureStorage();
    if (!secureStorage) {
        return;
    }

    await secureStorage.SecureStorage.set(
        OAUTH_REFRESH_TOKEN_KEY,
        refreshToken,
        true,
        false,
        secureStorage.KeychainAccess.whenUnlockedThisDeviceOnly
    );
}

async function clearStoredOAuthRefreshToken(): Promise<void> {
    const secureStorage = await getSecureStorage();
    if (!secureStorage) {
        return;
    }

    await secureStorage.SecureStorage.remove(OAUTH_REFRESH_TOKEN_KEY);
}

function parseLoginResponse(response: unknown) {
    if (
        !isNonNullableObject(response) ||
        (!("token" in response) && !("deviceToken" in response)) ||
        ("token" in response && typeof response.token !== "string") ||
        ("deviceToken" in response &&
            typeof response.deviceToken !== "string") ||
        !("userId" in response) ||
        (typeof response.userId !== "number" &&
            typeof response.userId !== "string")
    ) {
        throw new Error(
            `Unexpected token response: expected { token: string, userId: number } but got ${JSON.stringify(response)}`
        );
    }

    const token =
        "token" in response
            ? (response.token as string)
            : (response.deviceToken as string);
    const userId =
        typeof response.userId === "string"
            ? parseInt(response.userId, 10)
            : response.userId;

    return {
        token,
        id: userId,
        subscribed:
            "subscribed" in response ? Boolean(response.subscribed) : undefined
    };
}
export const useAuthUserStore = defineStore("authUser", {
    state: () => ({ ...initState }),
    getters: {
        isAuthenticated(): boolean {
            const appConfig = useAppConfigStore();
            return appConfig.features.authentication.enabled && !!this.token;
        },
        isSubscribed(): boolean {
            const appConfig = useAppConfigStore();

            // if subscriptions are disabled, treat all authenticated users as subscribed
            if (
                this.isAuthenticated &&
                appConfig.features.subscriptions.enabled === false
            ) {
                return true;
            }
            // receipt-only users have no account record but their token grants content access
            if (this.isAuthenticated && this.receiptAuthenticated) {
                return true;
            }
            return this.subscriptions.some(
                sub =>
                    sub.status === "active" || sub.status === "active_canceled"
            );
        },
        activeSubscriptions(): AuthUser["subscriptions"] {
            return this.subscriptions.filter(
                sub =>
                    sub.status === "active" || sub.status === "active_canceled"
            );
        }
    },
    actions: {
        async init() {
            const appConfig = useAppConfigStore();
            if (!appConfig.features.authentication.enabled) {
                return;
            }
            try {
                this.$api.addErrorInterceptor(async (error, context) => {
                    if (
                        error.status !== 401 ||
                        this.receiptAuthenticated ||
                        this.receiptLoginPending
                    ) {
                        return;
                    }

                    const currentHeaders =
                        context.request.config.headers instanceof Headers
                            ? Object.fromEntries(
                                  context.request.config.headers.entries()
                              )
                            : (context.request.config.headers ?? {});
                    const retryCount = Number(
                        currentHeaders["x-auth-retry"] ?? "0"
                    );
                    if (retryCount >= 1) {
                        this.logout();
                        return error;
                    }

                    const refreshedToken = await this.refreshAccessToken();
                    if (!refreshedToken) {
                        this.logout();
                        return error;
                    }

                    return await context.retry({
                        config: {
                            ...context.request.config,
                            headers: {
                                ...currentHeaders,
                                Authorization: `Bearer ${refreshedToken}`,
                                "x-auth-retry": "1"
                            }
                        }
                    });
                });
                // this.$notify.notify({ message: '[Auth] init: starting validateToken', timeout: 8000 });
                const hasValidToken = !!(
                    (await this.refreshAccessToken()) ||
                    (await this.validateToken())
                );
                // this.$notify.notify({ message: `[Auth] init: validateToken=${String(hasValidToken)}`, timeout: 8000 });
                if (hasValidToken) {
                    // this.$notify.notify({ message: '[Auth] init: calling loadUserData', timeout: 8000 });
                    await this.loadUserData();
                    // this.$notify.notify({ message: '[Auth] init: loadUserData done', timeout: 8000 });
                } else if (import.meta.env.QUASAR_CAPACITOR_MODE) {
                    // Receipt login involves slow Amazon IAP SDK init (~10-20s on FireTV).
                    // Fire it in background — router auth state is already correct at this point
                    // (unauthenticated). Free content renders immediately; subscriber content
                    // unlocks reactively once receipt auth completes.
                    void this.tryReceiptLogin().catch((e: unknown) => {
                        console.error("Receipt login failed:", e);
                    });
                }
            } catch (e) {
                const err = errorFromUnknown(e);
                console.error("Error initializing auth user store:", err);
            }
        },
        // This is also used to refresh the OTT API tokens when not using oauth
        async exchangeExternalAccessToken(accessToken: string, retryCount = 0) {
            const appConfig = useAppConfigStore();
            const response = await this.$api.post(
                "",
                {
                    cmd: "authenticate",
                    accountId: appConfig.accountId,
                    platform: appConfig.info.appStore
                },
                {
                    baseURL: "auth",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        ...(retryCount > 0
                            ? { "x-auth-retry": `${retryCount}` }
                            : {})
                    }
                }
            );

            const { token, id } = parseLoginResponse(response.data);
            this.id = id;
            this.setBearerToken(token);
            return token;
        },
        async createOAuthAuthorizationRequest(currentUrl: string) {
            const oauth = getOAuthConfigOrThrow();
            const discovery = await loadOAuthDiscovery();
            const state = createRandomString(64);
            const verifier = createRandomString(96);
            const challenge = await createPkceChallenge(verifier);
            const redirectUri = getOAuthRedirectUri(currentUrl);
            const params = new URLSearchParams({
                client_id: oauth.clientId,
                response_type: "code",
                redirect_uri: redirectUri,
                code_challenge: challenge,
                code_challenge_method: "S256",
                state
            });

            sessionStorage.setItem(OAUTH_STATE_KEY, state);
            sessionStorage.setItem(OAUTH_PKCE_VERIFIER_KEY, verifier);

            return {
                authorizationUrl: `${discovery.authorization_endpoint}?${params.toString()}`
            };
        },
        async handleOAuthAuthorizationCodeCallback(
            currentUrl: string
        ): Promise<{
            handled: boolean;
            clearedUrl?: string;
        }> {
            const routeQuery = Object.fromEntries(
                new URL(currentUrl).searchParams.entries()
            );
            const code = getOAuthCallbackParam("code", routeQuery, currentUrl);
            if (!code) {
                const callbackError = getOAuthCallbackParam(
                    "error",
                    routeQuery,
                    currentUrl
                );
                if (!callbackError) {
                    return { handled: false };
                }
                const description = getOAuthCallbackParam(
                    "error_description",
                    routeQuery,
                    currentUrl
                );
                throw oauthErrorFromResponse({
                    status: oauthStatusFromCode(callbackError),
                    data: {
                        error: callbackError,
                        error_description: description
                    }
                });
            }

            const receivedState = getOAuthCallbackParam(
                "state",
                routeQuery,
                currentUrl
            );
            const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY);
            const verifier = sessionStorage.getItem(OAUTH_PKCE_VERIFIER_KEY);
            if (
                !receivedState ||
                !expectedState ||
                receivedState !== expectedState ||
                !verifier
            ) {
                throw oauthErrorFromResponse({
                    status: 400,
                    data: {
                        error: "invalid_request",
                        error_description:
                            "OAuth callback state validation failed"
                    }
                });
            }

            await this.completeOAuthAuthorizationCodeLogin(
                code,
                verifier,
                getOAuthRedirectUri(currentUrl)
            );
            sessionStorage.removeItem(OAUTH_STATE_KEY);
            sessionStorage.removeItem(OAUTH_PKCE_VERIFIER_KEY);
            return {
                handled: true,
                clearedUrl: clearOAuthCallbackParams(currentUrl)
            };
        },
        async completeOAuthAuthorizationCodeLogin(
            code: string,
            verifier: string,
            redirectUri: string
        ) {
            const oauth = getOAuthConfigOrThrow();
            const discovery = await loadOAuthDiscovery();
            const body = new URLSearchParams({
                grant_type: "authorization_code",
                client_id: oauth.clientId,
                code,
                redirect_uri: redirectUri,
                code_verifier: verifier
            });

            if (oauth.audience) {
                body.set("audience", oauth.audience);
            }

            const response = await oauthPostForm(
                discovery.token_endpoint,
                body
            );
            const tokenData = parseOAuthTokenResponse(
                parseOAuthResponseOrThrow(response.status, response.data)
            );
            await this.loginWithOAuthToken(
                tokenData.accessToken,
                tokenData.refreshToken
            );
        },
        async requestOAuthDeviceCode(): Promise<OAuthDeviceAuthorizationResponse> {
            const oauth = getOAuthConfigOrThrow();
            const discovery = await loadOAuthDiscovery();
            if (!discovery.device_authorization_endpoint) {
                throw new AppError(
                    "OAuth provider does not expose device authorization endpoint.",
                    {
                        code: "incorrect_config"
                    }
                );
            }

            const body = new URLSearchParams({
                client_id: oauth.clientId
            });
            if (oauth.scopes.length > 0) {
                body.set("scope", oauth.scopes.join(" "));
            }
            if (oauth.audience) {
                body.set("audience", oauth.audience);
            }

            const response = await oauthPostForm(
                discovery.device_authorization_endpoint,
                body
            );
            const data = parseOAuthResponseOrThrow(
                response.status,
                response.data
            );

            if (
                !isNonNullableObject(data) ||
                !propGuard(data, "device_code", "string") ||
                !propGuard(data, "user_code", "string") ||
                !propGuard(data, "verification_uri", "string") ||
                !propGuard(data, "expires_in", "number")
            ) {
                throw new AppError(
                    "OAuth device authorization response is invalid.",
                    {
                        code: "parse_error"
                    }
                );
            }

            return {
                device_code: data.device_code,
                user_code: data.user_code,
                verification_uri: data.verification_uri,
                expires_in: data.expires_in,
                ...(propGuard(data, "verification_uri_complete", "string")
                    ? {
                          verification_uri_complete:
                              data.verification_uri_complete
                      }
                    : {}),
                ...(propGuard(data, "interval", "number")
                    ? { interval: data.interval }
                    : {})
            };
        },
        async pollOAuthDeviceToken(
            deviceResponse: OAuthDeviceAuthorizationResponse,
            abortSignal?: AbortSignal
        ) {
            const oauth = getOAuthConfigOrThrow();
            const discovery = await loadOAuthDiscovery();

            const expiresAt = Date.now() + deviceResponse.expires_in * 1000;
            let intervalSeconds = Math.max(2, deviceResponse.interval ?? 5);

            while (Date.now() < expiresAt) {
                if (abortSignal?.aborted) {
                    throw canceledError();
                }
                try {
                    await wait(intervalSeconds * 1000, abortSignal);
                } catch {
                    if (abortSignal?.aborted) {
                        throw canceledError();
                    }
                    throw new AppError(
                        "OAuth device polling failed unexpectedly.",
                        {
                            code: "unknown"
                        }
                    );
                }

                const body = new URLSearchParams({
                    grant_type: "urn:ietf:params:oauth:grant-type:device_code",
                    device_code: deviceResponse.device_code,
                    client_id: oauth.clientId
                });
                /*
                if (oauth.audience) {
                    body.set("audience", oauth.audience);
                }
                    */

                const { status, data } = await oauthPostForm(
                    discovery.token_endpoint,
                    body
                );
                if (status >= 200 && status < 300) {
                    const tokenData = parseOAuthTokenResponse(data);
                    await this.loginWithOAuthToken(
                        tokenData.accessToken,
                        tokenData.refreshToken
                    );
                    return;
                }

                const errorCode =
                    isNonNullableObject(data) &&
                    propGuard(data, "error", "string")
                        ? data.error
                        : undefined;
                if (errorCode === "authorization_pending") {
                    continue;
                }
                if (errorCode === "slow_down") {
                    intervalSeconds += 5;
                    continue;
                }

                throw oauthErrorFromResponse({ status, data });
            }

            throw oauthErrorFromResponse({
                status: 401,
                data: {
                    error: "expired_token",
                    error_description:
                        "Device authorization timed out. Please start over."
                }
            });
        },
        async refreshAccessToken() {
            const appConfig = useAppConfigStore();
            const oauth = appConfig.features.authentication.settings.oauth;
            if (!oauth?.discoveryUrl) {
                return null;
            }

            const refreshToken = await getStoredOAuthRefreshToken();
            if (!refreshToken) {
                return null;
            }

            try {
                const discovery = await loadOAuthDiscovery();

                const body = new URLSearchParams({
                    grant_type: "refresh_token",
                    client_id: oauth.clientId,
                    refresh_token: refreshToken
                });

                const response = await oauthPostForm(
                    discovery.token_endpoint,
                    body
                );
                const tokenData = parseOAuthTokenResponse(
                    parseOAuthResponseOrThrow(response.status, response.data)
                );
                const validatedToken = await this.exchangeExternalAccessToken(
                    tokenData.accessToken,
                    1
                );

                if (tokenData.refreshToken) {
                    await setStoredOAuthRefreshToken(tokenData.refreshToken);
                }

                return validatedToken;
            } catch (e) {
                console.error(
                    "Error refreshing OAuth access token:",
                    errorFromUnknown(e)
                );
                return null;
            }
        },
        async tryReceiptLogin() {
            // Dynamic import avoids circular dep: iap.ts already imports useAuthUserStore at top level
            const { useIAPStore } = await import("./iap");
            const iap = useIAPStore();
            const appConfig = useAppConfigStore();

            // Set pending flag BEFORE iap.init() so that any APPROVED transaction replay
            // triggered during initialization does not reach the purchase endpoint or
            // trigger logout via the 401 interceptor. Cleared in finally regardless of outcome.
            this.receiptLoginPending = true;
            // this.$notify.notify({ message: '[Auth] tryReceiptLogin: pending=true, calling iap.init()', timeout: 8000 });
            try {
                try {
                    await iap.init();
                    // this.$notify.notify({ message: '[Auth] tryReceiptLogin: iap.init() resolved', timeout: 8000 });
                } catch (e) {
                    const initErr = errorFromUnknown(e);
                    // this.$notify.notify({ message: `[Auth] tryReceiptLogin: iap.init() threw: ${initErr.message}`, timeout: 0, multiLine: true });
                    console.warn(
                        "[Auth] IAP init failed during receipt login, still attempting getActiveReceipt:",
                        initErr
                    );
                }

                // this.$notify.notify({ message: '[Auth] tryReceiptLogin: calling getActiveReceipt', timeout: 8000 });
                const active = await iap.getActiveReceipt();
                // this.$notify.notify({ message: `[Auth] tryReceiptLogin: getActiveReceipt=${active ? `platform=${active.platform}` : 'null'}`, timeout: 0, multiLine: true });
                if (!active) return;

                try {
                    // this.$notify.notify({ message: '[Auth] tryReceiptLogin: posting authenticate with receipt', timeout: 8000 });
                    const response = await this.$api.post(
                        "",
                        {
                            cmd: "authenticate",
                            accountId: appConfig.accountId,
                            platform: active.platform,
                            receipt: active.receipt
                        },
                        { baseURL: "auth" }
                    );
                    const { token, id } = parseLoginResponse(response.data);
                    this.id = id;
                    // The receipt is proof of subscription — mark authenticated unconditionally.
                    // There may be no user record (e.g. deleted); the token grants content
                    // access directly so we skip loadUserData().
                    this.receiptAuthenticated = true;
                    await clearStoredOAuthRefreshToken();
                    this.setBearerToken(token);
                    // this.$notify.notify({ message: `[Auth] tryReceiptLogin: authenticate OK — id=${String(id)} token=${token ? 'set' : 'null'}`, timeout: 0, multiLine: true });
                } catch (e) {
                    const authErr = errorFromUnknown(e);
                    // this.$notify.notify({ message: `[Auth] tryReceiptLogin: authenticate FAILED — ${authErr.message} (status=${String(authErr.status)})`, timeout: 0, multiLine: true });
                    console.warn(
                        "[Auth] Receipt-based login failed, continuing as guest:",
                        authErr
                    );
                }
            } finally {
                this.receiptLoginPending = false;
                // this.$notify.notify({ message: '[Auth] tryReceiptLogin: pending=false (finally)', timeout: 8000 });
            }
        },

        setBearerToken(token: string) {
            this.token = token;
            // Receipt-only tokens are not accepted by validateToken() on restart (backend
            // rejects device tokens with no associated user record). Skipping LocalStorage
            // here means validateToken() finds nothing on the next cold start and falls
            // through directly to tryReceiptLogin() — no wasted 401 round-trip.
            // receiptAuthenticated is set to true before this is called in tryReceiptLogin().
            if (!this.receiptAuthenticated) {
                LocalStorage.setItem("authToken", token);
            }
            this.$api.setBearerToken(token);
            void this.$api.configure({
                user: {
                    isAuthenticated: this.isAuthenticated,
                    isSubscribed: this.isSubscribed
                }
            });
        },
        async userExists(email: string) {
            try {
                await this.$api.post(
                    "",
                    {
                        cmd: "signup",
                        accountId: useAppConfigStore().accountId,
                        platform: useAppConfigStore().info.appStore,
                        email,
                        trial_run: true
                    },
                    { baseURL: "auth" }
                );

                return false;
            } catch (e) {
                const err = errorFromUnknown(e);
                if (err.status === 409 || err.code === "user_exists") {
                    return true;
                }
                console.error("Error checking if user exists:", err);
                throw err;
            }
        },
        async signup(
            email: string,
            password: string,
            customAnswers?: Record<number, string>
        ) {
            const appConfig = useAppConfigStore();

            try {
                const recaptchaToken =
                    await this.$executeRecaptchaAction("signup");
                const response = await this.$api.post(
                    "",
                    {
                        cmd: "signup",
                        accountId: appConfig.accountId,
                        platform: appConfig.info.appStore,
                        email,
                        password,
                        captcha: recaptchaToken,
                        ...(customAnswers ? { customAnswers } : {})
                    },
                    { baseURL: "auth" }
                );
                const { token, id } = parseLoginResponse(response.data);
                this.id = id;
                await clearStoredOAuthRefreshToken();
                this.setBearerToken(token);
                await this.loadUserData();
            } catch (e) {
                const err = errorFromUnknown(e);
                console.error("Error during signup:", err);
                throw err;
            }
        },
        logout() {
            this.$state = { ...initState };
            LocalStorage.removeItem("authToken");
            LocalStorage.removeItem("authUser");
            void clearStoredOAuthRefreshToken();
            this.$api.setBearerToken(undefined);
            void this.$api.configure({
                user: {
                    isAuthenticated: this.isAuthenticated,
                    isSubscribed: this.isSubscribed
                }
            });
        },
        async login(username: string, password: string) {
            const appConfig = useAppConfigStore();
            try {
                const response = await this.$api.post(
                    "",
                    {
                        cmd: "authenticate",
                        accountId: appConfig.accountId,
                        platform: appConfig.info.appStore,
                        username,
                        password
                    },
                    { baseURL: "auth" }
                );
                const { token, id } = parseLoginResponse(response.data);
                this.id = id;
                await clearStoredOAuthRefreshToken();
                this.setBearerToken(token);
                await this.loadUserData();
            } catch (e) {
                const err = errorFromUnknown(e);
                if (err.code === "unauthorized") {
                    err.display = "Invalid username or password";
                }
                throw err;
            }
        },
        async loginWithOAuthToken(accessToken: string, refreshToken?: string) {
            try {
                this.receiptAuthenticated = false;
                if (await this.validateToken(accessToken)) {
                    if (refreshToken) {
                        await setStoredOAuthRefreshToken(refreshToken);
                    } else {
                        await clearStoredOAuthRefreshToken();
                    }
                    await this.loadUserData();
                } else {
                    throw new APIError("Invalid OAuth token", {
                        code: "unauthorized"
                    });
                }
            } catch (e) {
                this.logout();
                throw errorFromUnknown(e);
            }
        },
        async initResetPassword(email: string) {
            const appConfig = useAppConfigStore();
            try {
                await this.$api.post(
                    "",
                    {
                        cmd: "reset_password",
                        email,
                        accountId: appConfig.accountId,
                        platform: appConfig.info.appStore
                    },
                    { baseURL: "auth" }
                );
            } catch (e) {
                const err = errorFromUnknown(e);
                console.error("Error initiating password reset:", err);
                throw err;
            }
        },
        async validateToken(rawToken?: string) {
            const storedToken =
                rawToken ?? LocalStorage.getItem<string>("authToken");
            if (storedToken) {
                try {
                    await this.exchangeExternalAccessToken(storedToken);
                    return true;
                } catch (e) {
                    const err = errorFromUnknown(e);
                    console.error("Error validating auth token:", err);
                    this.logout();
                }
            }
            return false;
        },
        async loadUserData() {
            if (!this.token) {
                return false;
            }
            try {
                const userData = (
                    await this.$api.get("", {
                        params: {
                            cmd: "getUser"
                        },
                        baseURL: "auth",
                        withToken: true,
                        type: "AuthUser"
                    })
                ).data;
                this.$state = { ...this.$state, ...userData };
                LocalStorage.setItem("authUser", userData);
                void this.$api.configure({
                    user: {
                        isAuthenticated: this.isAuthenticated,
                        isSubscribed: this.isSubscribed
                    }
                });
                return true;
            } catch (e) {
                const err = errorFromUnknown(e);
                console.error("Error loading user data:", err);
                this.logout();
                throw err;
            }
        }
    }
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAuthUserStore, import.meta.hot));
}
